import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { X } from "lucide-react";
import { ExternalContactCard } from "../external-contact-card/external-contact-card";
import { SystemCard } from "../system-card/system-card";
import { OrgFunctionCard } from "../org-function-card/org-function-card";
import { UserCard, toUserCardProps } from "../user-card/user-card";
import { resolveEntityRefDisplay } from "./suggestionNames";
import { ProposedEntityNameLabel } from "./ProposedEntityName";
function MissingEntity({ id }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "text-xs text-fg-subtle",
        children: id
    });
}
function PendingEntity({ label }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "text-xs italic text-fg-subtle",
        children: label
    });
}
// Each team/system entry is an EntityOrSuggestionRef — the shared resolver
// decides the branch; resolved entities keep their rich cards (the card
// derives its own deleted styling from the lookup entity's tombstone).
function refRow(ref, byId, renderEntity, pendingLabel, suggestionInfoById, t) {
    const row = resolveEntityRefDisplay(ref, {
        suggestionInfoById,
        pendingLabel,
        getEntity: (id)=>byId?.get(id),
        t
    });
    switch(row.kind){
        case "proposed":
            return /*#__PURE__*/ _jsx(ProposedEntityNameLabel, {
                name: row.label,
                isDeclined: row.isDeclined,
                as: "div",
                className: "text-xs"
            });
        case "pending":
            return /*#__PURE__*/ _jsx(PendingEntity, {
                label: row.label
            });
        case "missing":
            return /*#__PURE__*/ _jsx(MissingEntity, {
                id: row.label
            });
        case "entity":
            return renderEntity(row.entity);
    }
}
function SuccessorRow({ children, onRemove, removeLabel }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "group/successor flex items-center gap-1 min-w-0",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "min-w-0 flex-1",
                children: children
            }),
            onRemove && /*#__PURE__*/ _jsx("button", {
                type: "button",
                "aria-label": removeLabel,
                title: removeLabel,
                onClick: (e)=>{
                    e.stopPropagation();
                    onRemove();
                },
                className: "p-0.5 text-fg-subtle opacity-0 group-hover/successor:opacity-100 hover:text-danger shrink-0",
                children: /*#__PURE__*/ _jsx(X, {
                    className: "w-3 h-3"
                })
            })
        ]
    });
}
export function SuccessorSummary({ value, assigneeLookup, t, onRemove, onClearStatus, suggestionInfoById }) {
    const removeLabel = t("processFlow.editable.removeSuccessor");
    if (value.successorStatus === "NONE" || value.successorStatus === "UNCLEAR") {
        const label = value.successorStatus === "NONE" ? t("processFlow.noSuccessors") : t("processFlow.unclearSuccessors");
        return /*#__PURE__*/ _jsx(SuccessorRow, {
            onRemove: onClearStatus,
            removeLabel: removeLabel,
            children: /*#__PURE__*/ _jsx("span", {
                className: "text-fg-subtle italic text-xs",
                children: label
            })
        });
    }
    const personRows = value.persons.map((id)=>{
        const u = assigneeLookup.users?.get(id);
        return {
            id,
            node: u ? /*#__PURE__*/ _jsx(UserCard, {
                user: toUserCardProps(u),
                variant: "name-only",
                size: "sm",
                className: "p-0"
            }) : /*#__PURE__*/ _jsx(MissingEntity, {
                id: id
            })
        };
    });
    const pendingOrgFunctionLabel = t("processFlow.pendingOrgFunction");
    const pendingSystemLabel = t("processFlow.pendingSystem");
    const orgFunctionRows = value.orgFunctions.map((ref)=>({
            id: ref.id,
            node: refRow(ref, assigneeLookup.orgFunctions, (orgFunction)=>/*#__PURE__*/ _jsx(OrgFunctionCard, {
                    orgFunction: orgFunction,
                    variant: "name-only",
                    size: "sm",
                    className: "p-0"
                }), pendingOrgFunctionLabel, suggestionInfoById, t)
        }));
    const systemRows = value.systems.map((ref)=>({
            id: ref.id,
            node: refRow(ref, assigneeLookup.systems, (system)=>/*#__PURE__*/ _jsx(SystemCard, {
                    system: system,
                    variant: "name-only",
                    size: "sm",
                    className: "p-0"
                }), pendingSystemLabel, suggestionInfoById, t)
        }));
    const externalContactRows = value.externalContacts.map((id)=>{
        const contact = assigneeLookup.externalContacts?.get(id);
        return {
            id,
            node: contact ? /*#__PURE__*/ _jsx(ExternalContactCard, {
                contact: contact,
                variant: "name-only",
                size: "sm",
                className: "p-0"
            }) : /*#__PURE__*/ _jsx(MissingEntity, {
                id: id
            })
        };
    });
    const sections = [
        {
            key: "p",
            type: "persons",
            rows: personRows
        },
        {
            key: "t",
            type: "orgFunctions",
            rows: orgFunctionRows
        },
        {
            key: "s",
            type: "systems",
            rows: systemRows
        },
        {
            key: "e",
            type: "externalContacts",
            rows: externalContactRows
        }
    ];
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-1",
        children: sections.flatMap((section)=>section.rows.map((row)=>/*#__PURE__*/ _jsx(SuccessorRow, {
                    onRemove: onRemove ? ()=>onRemove(section.type, row.id) : undefined,
                    removeLabel: removeLabel,
                    children: row.node
                }, `${section.key}-${row.id}`)))
    });
}

//# sourceMappingURL=SuccessorSummary.js.map